import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageComponent } from '../../../../../services/website.service';
import { WebsiteService } from '../../../../../services/website.service';
import { ProjectService, Project } from '../../../../../services/project.service';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

@Component({
  selector: 'app-projects-component',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './projects-component.component.html',
  styleUrls: ['./projects-component.component.scss']
})
export class ProjectsComponentComponent implements OnInit, OnDestroy, OnChanges {
  @Input() component!: PageComponent;
  @Input() mode: 'preview' | 'edit' = 'preview';
  @Input() isSelected: boolean = false;
  @Input() currentLocale: string = 'nl';
  @Output() styleChange = new EventEmitter<void>();
  @Output() removeProject = new EventEmitter<string>();

  private destroy$ = new Subject<void>();
  projects: Project[] = [];
  filteredProjects: Project[] = [];
  loading = false;

  constructor(
    private projectService: ProjectService,
    private websiteService: WebsiteService
  ) {}

  ngOnInit() {
    const selectedProjectIds = this.getSelectedProjectIds();
    if (selectedProjectIds && selectedProjectIds.length > 0) {
      this.loadProjects();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['component']) {
      const currentSelectedIds = this.getSelectedProjectIds();
      const previousSelectedIds = changes['component'].previousValue?.data?.['selectedProjectIds'] || [];
      
      const currentIdsStr = JSON.stringify(currentSelectedIds || []);
      const previousIdsStr = JSON.stringify(previousSelectedIds);
      
      if (currentIdsStr !== previousIdsStr) {
        this.loadProjects();
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects() {
    const selectedProjectIds = this.getSelectedProjectIds();
    
    if (!selectedProjectIds || selectedProjectIds.length === 0) {
      this.projects = [];
      this.filteredProjects = [];
      this.loading = false;
      return;
    }

    this.loading = true;
    this.projectService.getProjects()
      .pipe(
        takeUntil(this.destroy$),
        map(projects => {
          const currentDomain = this.websiteService.getCurrentDomain();
          if (currentDomain) {
            return projects.filter(p => {
              const domains = p['domains'] || [];
              return domains.includes(currentDomain);
            });
          }
          return projects;
        })
      )
      .subscribe({
        next: (projects) => {
          this.projects = projects;
          this.filteredProjects = this.applyFilters(projects);
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading projects:', error);
          this.loading = false;
        }
      });
  }

  hasSelectedProjects(): boolean {
    const selectedProjectIds = this.getSelectedProjectIds();
    return selectedProjectIds && selectedProjectIds.length > 0;
  }

  applyFilters(projects: Project[]): Project[] {
    let filtered = [...projects];
    
    const selectedProjectIds = this.getSelectedProjectIds();
    if (selectedProjectIds && selectedProjectIds.length > 0) {
      filtered = filtered.filter(p => selectedProjectIds.includes(p.id || ''));
    }

    // Sort by title
    filtered.sort((a, b) => {
      const titleA = this.getProjectTitle(a).toLowerCase();
      const titleB = this.getProjectTitle(b).toLowerCase();
      return titleA.localeCompare(titleB);
    });

    return filtered;
  }

  // Getters
  getViewType(): string {
    return this.component.data['viewType'] || 'grid';
  }

  getColumns(): number {
    return this.component.data['columns'] || 3;
  }

  getSelectedProjectIds(): string[] {
    return this.component.data['selectedProjectIds'] || [];
  }

  getShowDescription(): boolean {
    return this.component.data['showDescription'] !== false;
  }

  getShowCategory(): boolean {
    return this.component.data['showCategory'] !== false;
  }

  getHeading(): string {
    return this.component.data['heading'] || '';
  }

  // Styling getters
  getBackgroundType(): string {
    return this.component.data['backgroundType'] || 'solid';
  }

  getBackgroundColor(): string {
    return this.component.data['backgroundColor'] || '#ffffff';
  }

  getGradientFrom(): string {
    return this.component.data['gradientFrom'] || '#ffffff';
  }

  getGradientTo(): string {
    return this.component.data['gradientTo'] || '#ffffff';
  }

  getBackgroundImage(): string {
    return this.component.data['backgroundImage'] || '';
  }

  getTitlePosition(): string {
    return this.component.data['titlePosition'] || 'left';
  }

  getHeadingColor(): string {
    return this.component.data['headingColor'] || '#111827';
  }

  getTextColor(): string {
    return this.component.data['textColor'] || '#374151';
  }

  getParallax(): boolean {
    return this.component.data['parallax'] || false;
  }

  getSectionHeight(): string {
    return this.component.data['sectionHeight'] || '';
  }

  getHeightUnit(): string {
    return this.component.data['heightUnit'] || 'px';
  }

  getPreviewStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const backgroundType = this.getBackgroundType();

    if (backgroundType === 'solid') {
      styles['background-color'] = this.getBackgroundColor();
    } else if (backgroundType === 'gradient') {
      styles['background'] = `linear-gradient(to bottom, ${this.getGradientFrom()}, ${this.getGradientTo()})`;
    } else if (backgroundType === 'image') {
      const bgImage = this.getBackgroundImage();
      if (bgImage) {
        styles['background-image'] = `url(${bgImage})`;
        styles['background-size'] = 'cover';
        styles['background-position'] = 'center';
        if (this.getParallax()) {
          styles['background-attachment'] = 'fixed';
        }
      }
    }

    const sectionHeight = this.getSectionHeight();
    if (sectionHeight) {
      const unit = this.getHeightUnit();
      styles['min-height'] = `${sectionHeight}${unit}`;
    }

    return styles;
  }

  getTitlePositionClass(): string {
    const position = this.getTitlePosition();
    switch (position) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  }

  getProjectTitle(project: Project): string {
    const locale = this.currentLocale;
    return project[`title_${locale}`] || project.title_nl || project.title || 'Untitled Project';
  }

  getProjectDescription(project: Project): string {
    const locale = this.currentLocale;
    const desc = project[`description_${locale}`] || project.description_nl || project.description || '';
    // Strip HTML tags for preview
    return desc.replace(/<[^>]*>/g, '').substring(0, 150) + (desc.length > 150 ? '...' : '');
  }

  getProjectImage(project: Project): string {
    if (project.image) {
      return project.image;
    }
    if (project.gallery && project.gallery.length > 0) {
      return project.gallery[0].url;
    }
    return '';
  }

  getGridColumnsClass(): string {
    const cols = this.getColumns();
    const gridClasses: { [key: number]: string } = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    };
    return gridClasses[cols] || gridClasses[3];
  }

  onRemoveProject(projectId: string, event: Event) {
    event.stopPropagation();
    this.removeProject.emit(projectId);
  }
}
